/**
 * Modal de confirmación para cambiar estado de envío a "Entregado"
 */
import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    IconButton,
    Divider,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PublishedWithChangesIcon from '@mui/icons-material/PublishedWithChanges';

const ModalConfirmarEstado = ({ open, onClose, envio, onConfirm, loading = false }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const handleClose = () => {
        if (!loading) {
            onClose();
        }
    };

    if (!envio) return null;

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            fullScreen={isMobile}
            PaperProps={{
                sx: {
                    borderRadius: isMobile ? 0 : 2,
                    boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                    m: isMobile ? 0 : 2,
                },
            }}
        >
            <DialogTitle
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: { xs: 1, sm: 2 },
                    pb: { xs: 1.5, sm: 2 },
                    pt: { xs: 2, sm: 2.5 },
                    px: { xs: 2, sm: 3 },
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.5 } }}>
                    <Box
                        sx={{
                            width: { xs: 36, sm: 40 },
                            height: { xs: 36, sm: 40 },
                            borderRadius: '50%',
                            backgroundColor: '#fee',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                        }}
                    >
                        <PublishedWithChangesIcon
                            sx={{ color: '#cc071e', fontSize: { xs: 20, sm: 24 } }}
                        />
                    </Box>
                    <Typography
                        variant="h6"
                        sx={{
                            fontWeight: 700,
                            color: '#212529',
                            fontSize: { xs: '1.1rem', sm: '1.25rem' },
                        }}
                    >
                        Confirmar cambio de estado
                    </Typography>
                </Box>
                <IconButton
                    onClick={handleClose}
                    disabled={loading}
                    size="small"
                    sx={{
                        color: '#6c757d',
                        '&:hover': {
                            backgroundColor: 'rgba(0,0,0,0.04)',
                        },
                    }}
                >
                    <CloseIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                </IconButton>
            </DialogTitle>

            <Divider />

            <DialogContent
                sx={{
                    pt: { xs: 2, sm: 3 },
                    pb: { xs: 2, sm: 2 },
                    px: { xs: 2, sm: 3 },
                    flex: isMobile ? 1 : 'initial',
                }}
            >
                <Typography sx={{ mb: 2, color: '#495057' }}>
                    ¿Está seguro de que desea forzar el estado a <strong>"Entregado"</strong> para
                    este envío?
                </Typography>

                <Box
                    sx={{
                        p: 2,
                        backgroundColor: '#f8f9fa',
                        borderRadius: 1,
                        border: '1px solid #dee2e6',
                    }}
                >
                    <Box sx={{ mb: 1 }}>
                        <Typography
                            component="span"
                            sx={{ fontWeight: 600, color: '#495057', fontSize: 14 }}
                        >
                            Albarán:{' '}
                        </Typography>
                        <Typography component="span" sx={{ color: '#212529', fontSize: 14 }}>
                            {envio.serie}-{envio.albaran}
                        </Typography>
                    </Box>
                    <Box sx={{ mb: 1 }}>
                        <Typography
                            component="span"
                            sx={{ fontWeight: 600, color: '#495057', fontSize: 14 }}
                        >
                            Cliente:{' '}
                        </Typography>
                        <Typography component="span" sx={{ color: '#212529', fontSize: 14 }}>
                            {envio.nombre}
                        </Typography>
                    </Box>
                    <Box>
                        <Typography
                            component="span"
                            sx={{ fontWeight: 600, color: '#495057', fontSize: 14 }}
                        >
                            Estado actual:{' '}
                        </Typography>
                        <Typography component="span" sx={{ color: '#212529', fontSize: 14 }}>
                            {envio.tracking_descripcion || 'Sin estado'}
                        </Typography>
                    </Box>
                </Box>
            </DialogContent>

            <Divider />

            <DialogActions
                sx={{
                    p: { xs: 2, sm: 2.5 },
                    gap: { xs: 1, sm: 1.5 },
                    flexDirection: { xs: 'column-reverse', sm: 'row' },
                }}
            >
                <Button
                    variant="outlined"
                    onClick={handleClose}
                    disabled={loading}
                    fullWidth={isMobile}
                    sx={{
                        textTransform: 'none',
                        fontWeight: 600,
                        px: { xs: 2, sm: 2.5 },
                        py: { xs: 1.25, sm: 1 },
                        fontSize: { xs: 14, sm: 15 },
                        color: '#6c757d',
                        borderColor: '#6c757d',
                        '&:hover': {
                            borderColor: '#495057',
                            backgroundColor: 'rgba(0,0,0,0.04)',
                        },
                    }}
                >
                    Cancelar
                </Button>
                <Button
                    variant="contained"
                    onClick={onConfirm}
                    disabled={loading}
                    fullWidth={isMobile}
                    startIcon={<CheckCircleIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />}
                    sx={{
                        textTransform: 'none',
                        fontWeight: 600,
                        px: { xs: 2, sm: 2.5 },
                        py: { xs: 1.25, sm: 1 },
                        fontSize: { xs: 14, sm: 15 },
                        backgroundColor: '#198754',
                        '&:hover': {
                            backgroundColor: '#157347',
                        },
                        '&:disabled': {
                            backgroundColor: '#6c757d',
                            color: '#fff',
                        },
                    }}
                >
                    Confirmar
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ModalConfirmarEstado;
